import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import type { Entry } from "@/shared/contexts/EntriesContext";

export type FailedEntry = {
  id: string;
  error: string;
};

export type ExportResult = {
  success: boolean;
  records: { id: string }[];
  failedEntries: FailedEntry[];
  errorMessage: string | null;
};

interface ExportResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: ExportResult | null;
  /** Optional entries list used to display matter/description for failed entries */
  entries?: Entry[];
}

export const ExportResultDialog: React.FC<ExportResultDialogProps> = ({
  open,
  onOpenChange,
  result,
  entries,
}) => {
  if (!result) return null;

  const { success, records, failedEntries, errorMessage } = result;
  const isPartialSuccess = success && failedEntries.length > 0;
  const isTotalFailure = !success;

  const entryMap = React.useMemo(() => {
    if (!entries) return new Map<number, Entry>();
    return new Map(entries.map((e) => [e.id, e]));
  }, [entries]);

  const getEntryLabel = (id: string) => {
    const entry = entryMap.get(Number(id));
    if (entry) {
      const matter = entry.matter || "No matter";
      const desc = entry.description
        ? entry.description.length > 60
          ? entry.description.slice(0, 60) + "…"
          : entry.description
        : "No description";
      return { matter, desc };
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isTotalFailure
              ? "Export Failed"
              : isPartialSuccess
                ? "Export Partially Successful"
                : "Export Successful"}
          </DialogTitle>
          <DialogDescription>
            {isTotalFailure
              ? "No entries were exported."
              : isPartialSuccess
                ? `${records.length} ${records.length === 1 ? "entry" : "entries"} exported. ${failedEntries.length} failed.`
                : `${records.length} ${records.length === 1 ? "entry" : "entries"} exported successfully.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {errorMessage && (
            <div
              className={`rounded-md border p-3 ${
                isTotalFailure
                  ? "border-red-200 bg-red-50"
                  : "border-yellow-200 bg-yellow-50"
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  isTotalFailure ? "text-red-800" : "text-yellow-800"
                }`}
              >
                {errorMessage}
              </p>
            </div>
          )}

          {failedEntries.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                {failedEntries.length === 1
                  ? "The following entry could not be exported:"
                  : `The following ${failedEntries.length} entries could not be exported:`}
              </p>
              <div className="rounded-md border border-gray-200">
                {failedEntries.map((fe) => {
                  const label = getEntryLabel(fe.id);
                  return (
                    <div
                      key={fe.id}
                      className="border-b border-gray-100 px-3 py-2 last:border-b-0"
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-medium text-gray-800">
                          {label ? label.matter : `Entry #${fe.id}`}
                        </span>
                      </div>
                      {label && (
                        <p className="text-xs text-gray-500 truncate">
                          {label.desc}
                        </p>
                      )}
                      <p className="mt-0.5 text-sm text-red-600">{fe.error}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
